/** Static content for the ELPM Creator door — styles, voices, mastering uses, plain-language terms. */

export const STYLE_CHIPS = [
  { key: "gospel-reggae", label: "Gospel Reggae", prompt: "gospel reggae, one-drop groove, warm bass, organ, uplifting worship lyrics", gradient: "from-emerald-400 to-lime-300" },
  { key: "worship", label: "Worship", prompt: "modern worship, atmospheric pads, acoustic guitar, building chorus, heartfelt vocals", gradient: "from-sky-400 to-violet-400" },
  { key: "gospel-rnb", label: "Gospel R&B", prompt: "gospel R&B, smooth soulful vocals, lush harmonies, warm keys, laid-back groove", gradient: "from-fuchsia-400 to-rose-400" },
  { key: "reggae", label: "Reggae", prompt: "roots reggae, offbeat guitar chops, deep bass, dub echoes, sunny feel", gradient: "from-amber-300 to-emerald-400" },
  { key: "afrobeats", label: "Afrobeats", prompt: "afrobeats, log drums, syncopated percussion, bright synths, danceable", gradient: "from-orange-400 to-amber-300" },
  { key: "soul", label: "Soul", prompt: "classic soul, live band feel, horns, Hammond organ, powerful emotional vocals", gradient: "from-rose-400 to-amber-400" },
  { key: "contemporary-gospel", label: "Contemporary Gospel", prompt: "contemporary gospel, choir backing, piano-led, big dynamic chorus, joyful", gradient: "from-violet-400 to-fuchsia-400" },
  { key: "custom", label: "Custom", prompt: "", gradient: "from-slate-300 to-slate-500" },
];

export const MORE_STYLES = [
  { key: "pop", label: "Pop", prompt: "modern pop, catchy hooks, polished production, radio-ready", gradient: "from-pink-400 to-violet-400" },
  { key: "hiphop", label: "Hip-Hop", prompt: "hip-hop, boom-bap drums, deep bass, confident flow", gradient: "from-slate-300 to-amber-400" },
  { key: "trap", label: "Trap", prompt: "trap, 808s, hi-hat rolls, dark moody piano", gradient: "from-violet-500 to-slate-400" },
  { key: "rnb", label: "R&B", prompt: "contemporary R&B, silky vocals, smooth chords, slow groove", gradient: "from-rose-400 to-fuchsia-500" },
  { key: "rock", label: "Rock", prompt: "anthemic rock, electric guitars, big drums, soaring chorus", gradient: "from-red-400 to-orange-400" },
  { key: "country", label: "Country", prompt: "modern country, acoustic guitar, pedal steel, storytelling vocals", gradient: "from-amber-300 to-yellow-500" },
  { key: "edm", label: "EDM / Dance", prompt: "EDM, four-on-the-floor, big drops, euphoric synths", gradient: "from-cyan-400 to-blue-500" },
  { key: "lofi", label: "Lo-fi", prompt: "lo-fi hip-hop, mellow Rhodes, dusty drums, rainy vibe", gradient: "from-teal-300 to-indigo-400" },
  { key: "latin", label: "Latin", prompt: "latin pop, reggaeton rhythm, bright guitars, summer energy", gradient: "from-lime-300 to-orange-400" },
  { key: "dancehall", label: "Dancehall", prompt: "dancehall, riddim bounce, punchy drums, party energy", gradient: "from-yellow-300 to-green-500" },
  { key: "amapiano", label: "Amapiano", prompt: "amapiano, log drum bass, jazzy piano, spacious groove", gradient: "from-emerald-300 to-cyan-400" },
  { key: "cinematic", label: "Cinematic", prompt: "cinematic orchestral, strings, epic build, emotional", gradient: "from-indigo-400 to-slate-300" },
  { key: "jazz", label: "Jazz", prompt: "jazz, upright bass, brushed drums, warm piano, late-night feel", gradient: "from-amber-400 to-rose-400" },
];

export const ALL_STYLES = [...STYLE_CHIPS, ...MORE_STYLES];
export const styleByKey = (key) => ALL_STYLES.find((s) => s.key === key) || null;

export const VOICE_OPTIONS = [
  { key: "male", label: "Male", hint: "A male lead vocal" },
  { key: "female", label: "Female", hint: "A female lead vocal" },
  { key: "instrumental", label: "Instrumental", hint: "No vocals — music only" },
];

export const VOCAL_STYLES = ["Smooth", "Powerful", "Soft & intimate", "Raspy", "Choir-backed", "Rap flow"];
export const INSTRUMENTS = ["Piano", "Acoustic guitar", "Electric guitar", "Organ", "Strings", "Horns", "Synths", "Live drums", "808s", "Choir"];
export const SONG_LENGTHS = [
  { key: "auto", label: "Auto", secs: null },
  { key: "60", label: "1 min", secs: 60 },
  { key: "120", label: "2 min", secs: 120 },
  { key: "180", label: "3 min", secs: 180 },
  { key: "240", label: "4 min", secs: 240 },
];
export const KEYS = ["Auto", "C major", "G major", "D major", "A major", "E major", "F major", "Bb major", "Eb major",
  "A minor", "E minor", "B minor", "D minor", "G minor", "C minor", "F minor"];

/** "How will you use your song?" — plain-language mastering targets. */
export const MASTER_USES = [
  { key: "spotify", label: "Spotify / Apple Music", desc: "Balanced loudness that streaming services won't turn down.", lufs: -14, ceiling: -1.0, accent: "from-emerald-400 to-teal-400" },
  { key: "youtube", label: "YouTube", desc: "Clear and consistent for video and phone speakers.", lufs: -14, ceiling: -1.0, accent: "from-red-400 to-rose-400" },
  { key: "cd", label: "CD / Album", desc: "Full, competitive album loudness.", lufs: -9, ceiling: -1.0, accent: "from-amber-300 to-orange-400" },
  { key: "church", label: "Church Playback", desc: "Gentle dynamics for sanctuary sound systems.", lufs: -16, ceiling: -1.0, accent: "from-sky-400 to-violet-400" },
  { key: "performance", label: "Loud Performance Mix", desc: "Maximum impact for live shows and backing tracks.", lufs: -8, ceiling: -1.0, accent: "from-fuchsia-400 to-red-400" },
];
export const masterUseByKey = (key) => MASTER_USES.find((m) => m.key === key) || MASTER_USES[0];

export const EXPORT_FORMATS = [
  { key: "wav24", label: "WAV · 24-bit", bitDepth: 24 },
  { key: "wav16", label: "WAV · 16-bit", bitDepth: 16 },
];

/** Write → Generate → Record → Mix → Master → Export */
export const SPINE_STEPS = [
  { key: "write", label: "Write" },
  { key: "generate", label: "Generate" },
  { key: "record", label: "Record" },
  { key: "mix", label: "Mix" },
  { key: "master", label: "Master" },
  { key: "export", label: "Export" },
];

/** Studio navigation — the ELPM Creator sections. `exact` = only the path itself is "active". */
export const SIMPLE_SECTIONS = [
  { key: "dashboard", label: "Dashboard", to: "/create", exact: true },
  { key: "create", label: "Create Music", to: "/create/song", also: ["/create/project"] },
  { key: "vocals", label: "Vocal Recording", to: "/create/studio", also: ["/create/record"] },
  { key: "enhance", label: "Enhance Vocals", to: "/create/enhance" },
  { key: "myvoices", label: "My Voices", to: "/create/my-voices" },
  { key: "bgv", label: "Background Singers", to: "/create/background-singers" },
  { key: "exportaudio", label: "Export Audio", to: "/create/export-audio" },
  { key: "voices", label: "Voice Profiles", to: "/create/voices" },
  { key: "stems", label: "Stem Separation", intent: "stems" },
  { key: "master", label: "Mix & Master", to: "/create/master" },
  { key: "library", label: "My Music Library", to: "/create/projects" },
  { key: "settings", label: "Settings", to: "/settings" },
];

/** Footer / help links — ownership, commercial use, privacy, pricing, support. */
export const INFO_LINKS = [
  { key: "how", label: "How it works", to: "/about#how-it-works" },
  { key: "rights", label: "Your Music & Your Rights", to: "/about#ownership" },
  { key: "pricing", label: "Pricing & Passes", to: "/about#pricing" },
  { key: "privacy", label: "Privacy & Storage", to: "/about#privacy" },
  { key: "support", label: "Support", to: "/about#support" },
];

export const SUPPORT_EMAIL = "support@elpm-studio.com";

/** The four-step "how a song is made" walkthrough shown on the homepage. */
export const DEMO_STEPS = [
  { key: "write", title: "1 · Write", body: "Type or paste your lyrics — or just an idea and a title.", seconds: 4.2 },
  { key: "style", title: "2 · Pick a style", body: "Tap a style like Gospel Reggae or Worship and choose a male or female voice.", seconds: 4.2 },
  { key: "generate", title: "3 · Generate", body: "ELPM writes the music, sings the vocals and mixes it — about a minute.", seconds: 4.6 },
  { key: "ready", title: "4 · Your song is ready", body: "Play it, add your own voice, master it for Spotify or church, then download.", seconds: 5.2 },
];

/** Pro term → Creator plain language. */
export const CREATOR_TERMS = {
  "Stem Extraction": "Separate Song Parts",
  "Master Bus": "Final Sound",
  "Input Routing": "Choose Microphone",
  "Render": "Create Audio File",
  "LUFS Target": "Mastering Loudness",
  "FX Rack": "Vocal Effects",
  "Session": "Project",
  "Track Arm": "Ready to Record",
  "Bounce": "Export Song",
};

/** Generating checklist — ticks on a time schedule, final tick when the song lands. */
export const GENERATING_STEPS = [
  { key: "instruments", label: "Creating instruments", at: 0 },
  { key: "vocals", label: "Generating vocals", at: 18 },
  { key: "mixing", label: "Mixing", at: 40 },
  { key: "finalizing", label: "Finalizing", at: 60 },
];
